import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/api/http';
import { AdminPageHeader } from '@/components/admin/admin-shell';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const THERAPY_MODALITY_LABEL: Record<string, string> = {
  PSYCHOANALYST: 'Psicanalista',
  PSYCHOTHERAPIST: 'Psicoterapeuta (não-psicólogo)',
  INTEGRATIVE: 'Terapeuta Integrativo',
  HOLISTIC: 'Terapeuta Holístico',
  AROMATHERAPIST: 'Aromaterapeuta',
  FLORAL: 'Terapeuta Floral',
  FAMILY_CONSTELLATION: 'Constelação Familiar',
  HYPNOTHERAPIST: 'Hipnoterapeuta',
};

export const Route = createFileRoute('/admin/terapeutas/$id')({ component: AdminTherapistDetail });

const DOW = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const money = (v: any) => Number(v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

function AdminTherapistDetail() {
  const { id } = Route.useParams();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin-therapist', id],
    queryFn: () => api.get(`admin/therapists/${id}`).json<any>(),
  });

  const { data: me } = useQuery({
    queryKey: ['auth-me'],
    queryFn: () => api.get('auth/me').json<{ id: string; role: string }>(),
  });

  const approve = useMutation({
    mutationFn: () => api.patch(`admin/therapists/${id}/approve`).json<any>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-therapist', id] }),
  });
  const reject = useMutation({
    mutationFn: () => api.patch(`admin/therapists/${id}/reject`).json<any>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-therapist', id] }),
  });
  const toggleVerify = useMutation({
    mutationFn: (verified: boolean) =>
      api.patch(`admin/therapists/${id}/verify`, { json: { verified } }).json<any>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-therapist', id] }),
  });
  const setRole = useMutation({
    mutationFn: (role: 'ADMIN' | 'THERAPIST') =>
      api.patch(`admin/therapists/${id}/role`, { json: { role } }).json<any>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-therapist', id] }),
  });
  const setFocusNfe = useMutation({
    mutationFn: (enabled: boolean) =>
      api.patch(`admin/therapists/${id}/focus-nfe`, { json: { enabled } }).json<any>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-therapist', id] }),
  });

  const [rejectReason, setRejectReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);
  const crpVerify = useMutation({
    mutationFn: (body: { action: 'VERIFY' | 'REJECT'; reason?: string }) =>
      api.patch(`admin/therapists/${id}/crp-verify`, { json: body }).json<any>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-therapist', id] });
      setShowRejectInput(false);
      setRejectReason('');
    },
  });

  if (isLoading || !data) {
    return (
      <>
        <AdminPageHeader title="Profissional" />
        <div className="bg-white rounded-3xl p-12 animate-pulse h-64 border border-slate-100 mt-4" />
      </>
    );
  }

  return (
    <>
      <Link to="/admin/terapeutas" className="text-sm text-coral-500 hover:underline">← Voltar</Link>

      {/* Header com avatar + status + ações */}
      <header className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm mt-3 mb-5">
        <div className="flex items-start gap-4 flex-wrap">
          {data.avatarUrl || data.user?.image ? (
            <img src={data.avatarUrl ?? data.user.image} alt="" className="size-16 rounded-full object-cover ring-2 ring-teal-100" />
          ) : (
            <div className="size-16 rounded-full bg-gradient-to-br from-teal-300 to-teal-500 flex items-center justify-center text-white font-bold text-2xl">
              {(data.user?.name ?? '?').charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-extrabold text-slate-900">{data.user?.name}</h1>
            <p className="text-sm text-slate-500">{data.user?.email}</p>
            <div className="flex items-center gap-2 flex-wrap mt-2">
              <Badge className={data.isActive ? 'bg-teal-50 text-teal-700 border-teal-200' : 'bg-red-50 text-red-600 border-red-200'}>
                {data.isActive ? 'Ativo' : 'Inativo'}
              </Badge>
              <Badge className={data.isVerified ? 'bg-teal-50 text-teal-700 border-teal-200' : 'bg-amber-50 text-amber-700 border-amber-200'}>
                {data.isVerified ? '✓ Verificado' : 'Sem verificação'}
              </Badge>
              <Badge className="bg-slate-100 text-slate-700 border-slate-200">Plano {data.plan}</Badge>
              <Badge className="bg-slate-100 text-slate-700 border-slate-200">{data.personType}</Badge>
              {data.user?.isMaster ? (
                <Badge className="bg-amber-50 text-amber-800 border-amber-200">★ Master</Badge>
              ) : data.user?.role === 'ADMIN' ? (
                <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200">★ Admin</Badge>
              ) : null}
            </div>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <Button
              onClick={() => approve.mutate()}
              disabled={approve.isPending}
              className="bg-teal-500 hover:bg-teal-600 text-white"
            >
              {approve.isPending ? 'Aprovando...' : '✓ Aprovar'}
            </Button>
            <Button
              variant="outline"
              onClick={() => reject.mutate()}
              disabled={reject.isPending}
              className="text-red-600 hover:bg-red-50 border-red-200 hover:text-red-700"
            >
              {reject.isPending ? 'Rejeitando...' : '✕ Rejeitar/Suspender'}
            </Button>

            {/* Toggle role — desabilitado pra próprio admin ou master (em ADMIN_EMAILS) */}
            {(() => {
              const isSelf = me?.id === data.user?.id;
              const isMaster = !!data.user?.isMaster;
              const blocked = isSelf || isMaster;
              const title = isMaster
                ? 'Master admin (ADMIN_EMAILS) — não pode ser alterado'
                : isSelf
                  ? 'Você não pode alterar sua própria role'
                  : '';
              return data.user?.role === 'ADMIN' ? (
                <Button
                  variant="outline"
                  onClick={() => setRole.mutate('THERAPIST')}
                  disabled={setRole.isPending || blocked}
                  title={title}
                  className="text-slate-600 hover:bg-slate-50 border-slate-200"
                >
                  {setRole.isPending ? 'Alterando...' : '↓ Remover admin'}
                </Button>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => setRole.mutate('ADMIN')}
                  disabled={setRole.isPending || blocked}
                  title={title}
                  className="text-indigo-600 hover:bg-indigo-50 border-indigo-200 hover:text-indigo-700"
                >
                  {setRole.isPending ? 'Promovendo...' : '★ Tornar admin'}
                </Button>
              );
            })()}

            {/* Focus NFe — emissão fiscal automática (tem custo por CNPJ) */}
            {data.focusNfeEnabled ? (
              <Button
                variant="outline"
                onClick={() => {
                  if (confirm('Desabilitar emissão fiscal automática deste profissional? Ele voltará ao envio manual de NF.')) {
                    setFocusNfe.mutate(false);
                  }
                }}
                disabled={setFocusNfe.isPending}
                title={`Status: ${data.focusNfeStatus}`}
                className="text-amber-700 hover:bg-amber-50 border-amber-200"
              >
                {setFocusNfe.isPending ? 'Alterando...' : `🧾 Focus NFe: ${data.focusNfeStatus === 'ACTIVE' ? 'ativo' : 'ligado'} (desligar)`}
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => {
                  if (confirm('Habilitar emissão fiscal automática (Focus NFe)?\n\n⚠️ Cada CNPJ ativo consome cota do plano Focus (custo). O profissional verá uma aba pra enviar certificado A1 e dados fiscais.')) {
                    setFocusNfe.mutate(true);
                  }
                }}
                disabled={setFocusNfe.isPending}
                className="text-slate-600 hover:bg-slate-50 border-slate-200"
              >
                {setFocusNfe.isPending ? 'Habilitando...' : '🧾 Habilitar Focus NFe'}
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* F6 — verificação CRP (só psicólogos) */}
      {data.professionalCategory === 'PSYCHOLOGIST' && (
        <div className="mb-5"><Pane title="Verificação de CRP">
          <div className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <Item label="CRP" value={data.crpNumber || '—'} />
              <Item label="UF" value={data.crpRegion || '—'} />
              <Item
                label="Status"
                value={
                  data.crpVerificationStatus === 'VERIFIED' ? '✓ Verificado' :
                  data.crpVerificationStatus === 'REJECTED' ? '✕ Rejeitado' :
                  '⏳ Pendente'
                }
              />
              <Item label="Verificado em" value={data.crpVerifiedAt ? new Date(data.crpVerifiedAt).toLocaleString('pt-BR') : '—'} />
            </div>
            {data.crpRejectionReason && (
              <div className="bg-rose-50 border border-rose-200 rounded-md p-2 text-xs text-rose-800">
                <strong>Motivo da rejeição:</strong> {data.crpRejectionReason}
              </div>
            )}
            <div className="text-xs text-slate-500">
              Confira no <a href={`https://cadastro.cfp.org.br/`} target="_blank" rel="noopener noreferrer" className="text-teal-600 underline">Cadastro Nacional de Psicólogos</a> antes de aprovar.
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => crpVerify.mutate({ action: 'VERIFY' })}
                disabled={crpVerify.isPending || data.crpVerificationStatus === 'VERIFIED'}
                className="bg-teal-500 hover:bg-teal-600 text-white"
              >
                ✓ Marcar como verificado
              </Button>
              {!showRejectInput ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowRejectInput(true)}
                  disabled={crpVerify.isPending}
                >
                  ✕ Rejeitar
                </Button>
              ) : (
                <div className="w-full mt-2 space-y-2">
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    rows={3}
                    minLength={10}
                    maxLength={500}
                    placeholder="Motivo da rejeição (mín. 10 caracteres). Será visível ao profissional."
                    className="w-full border border-slate-200 rounded-md p-2 text-sm"
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => crpVerify.mutate({ action: 'REJECT', reason: rejectReason })}
                      disabled={crpVerify.isPending || rejectReason.trim().length < 10}
                      className="bg-rose-500 hover:bg-rose-600 text-white"
                    >
                      Confirmar rejeição
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setShowRejectInput(false); setRejectReason(''); }}>
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}
            </div>
            {crpVerify.isError && <p className="text-xs text-rose-600">Erro ao atualizar. Tente novamente.</p>}
          </div>
        </Pane></div>
      )}

      <div className="grid lg:grid-cols-2 gap-5">
        <Pane title="Dados profissionais">
          <Item label="Bio" value={data.bio || '—'} />
          <Item
            label="Categoria"
            value={
              data.professionalCategory === 'PSYCHOLOGIST' ? 'Psicólogo(a) — regulamentada' :
              data.professionalCategory === 'THERAPIST' ? `Terapeuta — ${THERAPY_MODALITY_LABEL[data.therapyModality] ?? data.therapyModality ?? '—'}` :
              '— (não definida)'
            }
          />
          <Item label="Modalidade" value={data.modality} />
          <Item label="Sessão" value={`${data.sessionDuration ?? 50} min · ${money(data.sessionPrice)}`} />
          <Item label="Cidade/UF" value={[data.city, data.state].filter(Boolean).join(' / ') || '—'} />
          {data.specialties?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Especialidades</p>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {data.specialties.map((s: string) => (
                  <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-coral-50 text-coral-700 border border-coral-100">{s}</span>
                ))}
              </div>
            </div>
          )}
        </Pane>

        <Pane
          title="Dados fiscais"
          action={
            <Button
              size="sm"
              variant="outline"
              onClick={() => toggleVerify.mutate(!data.isFiscalDocVerified)}
              disabled={toggleVerify.isPending}
              className="text-xs h-7"
            >
              {data.isFiscalDocVerified ? 'Remover verificação' : 'Marcar como verificado'}
            </Button>
          }
        >
          <Item label="Tipo" value={data.personType} />
          <Item label="CNPJ" value={data.cnpj || '—'} />
          <Item label="Razão social" value={data.razaoSocial || '—'} />
          <Item label="Inscrição municipal" value={data.inscricaoMunicipal || '—'} />
          <Item label="Regime tributário" value={data.taxRegime || '—'} />
          <Item label="Verificação fiscal" value={data.isFiscalDocVerified ? '✓ Aprovada' : 'Pendente'} />
        </Pane>

        <Pane title="Endereço">
          <Item label="CEP" value={data.zipCode || '—'} />
          <Item label="Logradouro" value={data.street || '—'} />
          <Item label="Cidade" value={data.city || '—'} />
          <Item label="UF" value={data.state || '—'} />
        </Pane>

        <Pane title="Disponibilidade semanal">
          {(data.availability ?? []).length === 0 ? (
            <p className="text-sm text-slate-400">Não cadastrada.</p>
          ) : (
            <ul className="space-y-1.5">
              {data.availability.map((a: any) => (
                <li key={a.id} className="flex items-center justify-between text-sm">
                  <span className="text-slate-700 font-medium">{DOW[a.dayOfWeek]}</span>
                  <span className="text-slate-500 tabular-nums">{a.startTime} – {a.endTime}</span>
                </li>
              ))}
            </ul>
          )}
        </Pane>

        <Pane title="Dados bancários">
          {!data.bankAccount ? (
            <p className="text-sm text-slate-400">Não cadastrados.</p>
          ) : (
            <>
              <Item label="Banco" value={data.bankAccount.bankCode || '—'} />
              <Item label="Agência" value={data.bankAccount.agency || '—'} />
              <Item label="Conta" value={`${data.bankAccount.accountNumber} (${data.bankAccount.accountType})`} />
              {data.bankAccount.pixKey && (
                <Item label={`Chave PIX (${data.bankAccount.pixKeyType ?? '—'})`} value={data.bankAccount.pixKey} />
              )}
            </>
          )}
        </Pane>

        <Pane title="Documentos">
          {(data.documents ?? []).length === 0 ? (
            <p className="text-sm text-slate-400">Nenhum documento enviado.</p>
          ) : (
            <ul className="space-y-2">
              {data.documents.map((doc: any) => (
                <li key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-md gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{doc.name}</p>
                    <p className="text-xs text-slate-500">{doc.fileType}</p>
                  </div>
                  <a href={doc.fileUrl} target="_blank" rel="noreferrer" className="text-xs text-teal-600 hover:underline shrink-0">
                    Ver →
                  </a>
                </li>
              ))}
            </ul>
          )}
        </Pane>
      </div>
    </>
  );
}

function Pane({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm">
      <header className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-slate-700">{title}</h3>
        {action}
      </header>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Item({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
      <p className="text-sm text-slate-700 mt-0.5 whitespace-pre-line">{value}</p>
    </div>
  );
}

function Badge({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={cn('text-[11px] px-2 py-0.5 rounded border font-medium whitespace-nowrap', className)}>
      {children}
    </span>
  );
}
